"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import useStore from "@/store/useStore";

export default function SubscriptionReactivationNeeded({
  spaceSlug,
  error,
  isOwner = false,
}) {
  const router = useRouter();
  const session = useStore((state) => state.session);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Auto-redirect owners to billing after a short delay
  useEffect(() => {
    if (isOwner && session?.user) {
      const redirectTimer = setTimeout(() => {
        setIsRedirecting(true);
        router.push("/user/settings/billing");
      }, 5000); // 5 second delay to show the message

      return () => clearTimeout(redirectTimer);
    }
  }, [isOwner, session, router]);

  const handleReactivateClick = () => {
    if (session?.user) {
      setIsRedirecting(true);
      router.push("/user/settings/billing");
    } else {
      // If not logged in, redirect to sign in first
      router.push("/signin");
    }
  };

  const handleClaimClick = () => {
    if (typeof window !== "undefined" && window.umami) {
      window.umami.track("space_claim_referral", {
        source: "subscription_reactivation_page",
        slug: spaceSlug,
        type: "subscription_expired",
      });
    }
    router.push(`/onboarding?slug=${spaceSlug}`);
  };

  if (isRedirecting) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 text-center bg-gradient-to-b from-blue-600 to-indigo-700">
        <Image
          src="/logos/beta/white.svg"
          alt="Logo"
          width={180}
          height={60}
          className="mb-8"
        />
        <div className="bg-white/10 rounded-lg p-8 backdrop-blur-sm">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-white animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">
              Redirecting...
            </h1>
            <p className="text-white/80">Taking you to billing settings.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 text-center bg-gradient-to-b from-orange-600 to-red-700">
      <Image
        src="/logos/beta/white.svg"
        alt="Logo"
        width={180}
        height={60}
        className="mb-8"
      />

      <h1 className="text-4xl font-bold mb-4 text-white">
        {isOwner ? "Subscription Required" : "Space Available"}
      </h1>

      <div className="bg-white/10 rounded-lg px-8 py-4 mb-8 backdrop-blur-sm">
        <p className="text-3xl font-mono text-white">
          qensa.me/<span className="text-yellow-400">{spaceSlug}</span>
        </p>
      </div>

      {!isOwner ? (
        <>
          <div className="space-y-4">
            <button
              onClick={handleClaimClick}
              className="inline-block px-12 py-5 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-xl font-bold text-lg hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 hover:scale-105 transform shadow-2xl"
            >
              🚀 Claim This Space
            </button>

            <p className="text-white/70 text-sm">
              Already have an account?{" "}
              <Link
                href="/signin"
                className="text-yellow-400 underline hover:text-yellow-300 font-semibold"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </>
      ) : (
        <p className="text-xl text-white/80 mb-6 max-w-lg">
          Your space was unpublished due to subscription issues. Reactivate to
          restore access.
        </p>
      )}

      {/* Owner access section */}
      <div className="mt-8 pt-6 border-t border-white/20 max-w-lg">
        {!isOwner && (
          <>
            <h3 className="text-sm font-semibold text-white mb-3">
              Are you the original owner?
            </h3>
            <button
              onClick={handleReactivateClick}
              className="inline-block px-6 py-3 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors border border-white/30"
            >
              Reactivate Subscription
            </button>
          </>
        )}

        {isOwner && (
          <div className="flex flex-col items-center space-y-4">
            <button
              onClick={handleReactivateClick}
              className="inline-block px-8 py-4 bg-white text-orange-600 rounded-lg font-semibold hover:bg-white/90 transition-colors"
            >
              Go to Billing Settings
            </button>

            <p className="text-white/60 text-sm">
              Auto-redirecting in a few seconds...
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
